import { type FC, useState } from "react";
import { usePouch, useView } from "use-pouchdb";
import { useNavigate } from "react-router";

import { assertIsItemInfo, isItemInfo } from "../../common/schema";
import { useScanner } from "../Scanner";
import { ItemItem } from "../ItemItem";

export const ItemList:FC = () => {
  const { rows, state, error } = useView("built/not_deleted", { include_docs: true, limit: 500 });
  const db = usePouch();
  const navigate = useNavigate();
  const [scanError, setScanError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const { startScanner: startSearchScanner } = useScanner({
    onScanResult: async (result) => {
      if (result == null) return;
      setScanError(null);
      try {
        const found = await db.query("built/item_by_shelcode", { key: result.text, include_docs: true });
        const row = found.rows[0];
        if (row?.id != null) {
          navigate(`/items/${row.id}`);
        } else {
          setScanError(`No item with shelcode ${result.text}`);
        }
      } catch (e) {
        setScanError(`Scan lookup failed: ${e}`);
      }
    },
  });

  if (state === "error") {
    return <p>Error: {`${error}`}</p>
  }

  return <div style={{display: "flex", flexDirection: "column"}}>
    <div style={{display: "flex", alignItems: "center", gap: "8px", marginLeft: "3px"}}>
      <h3 style={{margin: "0"}}>Items</h3>
      <input
        type="search"
        placeholder="Search by name or shelcode…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        data-testid="item-search"
        style={{flexGrow: 1}}
      />
      <button type="button" onClick={startSearchScanner} title="Find item by scan">🔍📷</button>
    </div>
    {scanError && <p style={{color: "red", marginLeft: "3px", fontSize: "small"}}>{scanError}</p>}
    {[...rows]
      .sort((a, b) => {
        // UUID v7 IDs begin with "item:" followed by a hex digit.
        // Non-UUID IDs (e.g. item:memento_import:…) sort after all UUID items.
        const aUuid = /^item:[0-9a-f]/i.test(a.id);
        const bUuid = /^item:[0-9a-f]/i.test(b.id);
        if (aUuid !== bUuid) return aUuid ? -1 : 1;
        return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
      })
      .filter(row => {
        if (!isItemInfo(row.doc)) return false;
        if (row.doc.is_template) return false;
        if (search === "") return true;
        const q = search.toLowerCase();
        return row.doc.name.toLowerCase().includes(q) || (row.doc.shelcode ?? "").includes(q);
      }).map((row) => {
        assertIsItemInfo(row.doc);
        return <ItemItem key={row.doc._id} doc={row.doc} />;
      })}
  </div>;
};
